/**
 * Ikigai, for a twelve-year-old.
 *
 * The 12–14 band's job is NOT to hand out job titles. It's to plant the one
 * idea that keeps working for the next thirty years: good work sits where
 * what you love, what you're good at, what the world needs, and what people
 * will value all overlap.
 *
 * Presentation only. Every value here is handed in by the result page —
 * this file contains no scoring, no ranking, no filtering.
 */
import { Heart, Sparkles, Globe2, HandCoins } from "lucide-react";

export interface IkigaiKidProps {
  /** Plain-language things the learner said they enjoy. */
  loves: string[];
  /** Their archetype, e.g. "Builder". */
  archetypePrimary: string;
  /** One sentence about what that archetype means. */
  archetypeTag: string;
  /** Their best-fitting world name + blurb — stands in for "what the world needs". */
  worldName: string;
  worldBlurb: string;
}

export function IkigaiKid({
  loves, archetypePrimary, archetypeTag, worldName, worldBlurb,
}: IkigaiKidProps) {
  const lovesLine = loves.length
    ? loves.slice(0, 4).join(", ")
    : "the things you keep coming back to";

  const quadrants = [
    {
      icon: Heart,
      label: "What you love",
      body: `Right now that looks like ${lovesLine}. Liking something isn't a promise — it's a clue.`,
    },
    {
      icon: Sparkles,
      label: "What you're good at",
      body: `You're a ${archetypePrimary} — ${archetypeTag} You'll get better at this the more you use it.`,
    },
    {
      icon: Globe2,
      label: "What the world needs",
      body: `${worldBlurb} That's the world of ${worldName.toLowerCase()}.`,
    },
    {
      icon: HandCoins,
      label: "What people will value",
      body: "When you help with something people really need, they look after you back. That's what a job is.",
    },
  ];

  return (
    <section aria-labelledby="ikigai-heading" data-testid="ikigai-kid">
      <h2 id="ikigai-heading" className="font-display text-2xl font-medium">
        Where your best work lives
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        In Japan there's a word for this: <span className="text-foreground/90">ikigai</span> —
        your reason to get up in the morning. It sits where these four things overlap.
      </p>

      <ol className="mt-5 grid gap-3 sm:grid-cols-2">
        {quadrants.map(({ icon: Icon, label, body }, i) => (
          <li
            key={label}
            className="rounded-3xl border border-border bg-card p-5"
          >
            <div className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-2xl bg-primary/15">
                <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
              </span>
              <h3 className="font-display text-base font-medium">
                <span className="sr-only">Part {i + 1} of 4: </span>{label}
              </h3>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-foreground/90">{body}</p>
          </li>
        ))}
      </ol>

      <p className="mt-4 rounded-3xl border border-primary/25 bg-primary/5 p-5 text-sm leading-relaxed text-foreground/90">
        You don't have to find the middle of these four today. You just have to keep noticing
        them. Every time you help someone with something only you were willing to do — that's
        the middle, showing up early.
      </p>
    </section>
  );
}

export default IkigaiKid;
